import { datadogRum } from '@datadog/browser-rum';
import { getIsDevelopment } from '../constants/environment';

/**
 * Initialize Datadog RUM with the given configuration
 * @returns {void}
 */
/**
 * Check if Datadog RUM is initialized and available
 * @returns {boolean}
 */
const isDatadogRumAvailable = () => {
  return (
    typeof datadogRum !== 'undefined' &&
    datadogRum &&
    typeof datadogRum.addAction === 'function'
  );
};

// Helper function to escape regex special characters
function escapeRegex(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Helper function to create URL regex from URL
function createUrlRegex(url) {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    const host = urlObj.host; // includes port if present

    // For localhost, keep it simple
    if (host.includes('localhost')) {
      const origin = urlObj.origin;
      const escapedOrigin = escapeRegex(origin);
      return new RegExp(`^${escapedOrigin}\\/.*$`);
    }

    // For other domains, create a flexible pattern that matches subdomains
    const hostParts = host.split('.');
    if (hostParts.length >= 2) {
      // Extract domain and TLD (e.g., "fortress.engineering" from "api-payments-dev.fortress.engineering")
      const domain = hostParts.slice(-2).join('.');
      const escapedDomain = escapeRegex(domain);
      return new RegExp(
        `^https?:\\/\\/([a-zA-Z0-9-]+\\.)*${escapedDomain}\\/.*$`,
      );
    }

    // Fallback to exact match
    const origin = urlObj.origin;
    const escapedOrigin = escapeRegex(origin);
    return new RegExp(`^${escapedOrigin}\\/.*$`);
  } catch {
    // Fallback for invalid URLs
    const escapedUrl = escapeRegex(url.replace(/\/.*$/, ''));
    return new RegExp(`^${escapedUrl}\\/.*$`);
  }
}

// Helper function to create a more flexible origin regex that matches subdomains
function createFlexibleOriginRegex(url) {
  if (!url) return null;
  try {
    const urlObj = new URL(url);
    const host = urlObj.host; // includes port if present

    // For localhost, keep it simple
    if (host.includes('localhost')) {
      const escapedHost = escapeRegex(host);
      return new RegExp(`^https?:\\/\\/${escapedHost}$`);
    }

    // For other domains, create a flexible pattern that matches subdomains
    const hostParts = host.split('.');
    if (hostParts.length >= 2) {
      // Extract domain and TLD (e.g., "fortress.engineering" from "api-payments-dev.fortress.engineering")
      const domain = hostParts.slice(-2).join('.');
      const escapedDomain = escapeRegex(domain);
      return new RegExp(`^https?:\\/\\/([a-zA-Z0-9-]+\\.)*${escapedDomain}$`);
    }

    // Fallback to exact match
    const escapedHost = escapeRegex(host);
    return new RegExp(`^https?:\\/\\/${escapedHost}$`);
  } catch {
    // Fallback for invalid URLs
    const escapedUrl = escapeRegex(
      url.replace(/^https?:\/\//, '').replace(/\/.*$/, ''),
    );
    return new RegExp(`^https?:\\/\\/${escapedUrl}$`);
  }
}

// Generate allowedTracingOrigins from environment variables
const allowedTracingOrigins = [
  // Local development fallback
  new RegExp(`^https?:\\/\\/localhost:\\d+$`),
  // API origins from environment variables (using flexible matching)
  createFlexibleOriginRegex(process.env.REACT_APP_API_URL),
  createFlexibleOriginRegex(process.env.REACT_APP_PAYMENTS_API_URL),
].filter(Boolean);

// Generate allowedTracingUrls from environment variables
const allowedTracingUrls = [
  // Local development URLs
  new RegExp(`^https?:\\/\\/localhost:\\d+\\/.*$`),
  // API URLs from environment variables
  createUrlRegex(process.env.REACT_APP_API_URL),
  createUrlRegex(process.env.REACT_APP_PAYMENTS_API_URL),
].filter(Boolean);

export const initializeDatadogRum = () => {
  if (
    !process.env.REACT_APP_DATADOG_APPLICATION_ID ||
    !process.env.REACT_APP_DATADOG_CLIENT_TOKEN
  ) {
    console.warn(
      '❌ Datadog RUM not initialized: Missing required environment variables',
    );
    return;
  }
  try {
    datadogRum.init({
      applicationId: process.env.REACT_APP_DATADOG_APPLICATION_ID,
      clientToken: process.env.REACT_APP_DATADOG_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: process.env.REACT_APP_DATADOG_SERVICE || 'fortress-portal',
      env:
        process.env.REACT_APP_NODE_ENV === 'ephemeral'
          ? 'development'
          : process.env.REACT_APP_NODE_ENV || 'development',
      version: process.env.REACT_APP_VERSION || '1.0.0',
      trackInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      allowedTracingOrigins,
      allowedTracingUrls,
      enableExperimentalFeatures: ['feature_flags'],
      enablePrivacyForActionName: false,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      traceSampleRate: 100,
      defaultPrivacyLevel: 'allow',
      trackFrustrations: true,
      trackUserInteractions: true,
      trackErrors: true,
      beforeSend: (event) => {
        if (event.view.url) {
          const pathParts = event.view.url.split('/').filter((part) => part); // split and remove empty parts
          const section = pathParts[4];
          const subSection = pathParts[5];
          const pageIdentifier = section
            ? `${section}${subSection ? `/${subSection}` : ''}`
            : 'baseURL'; // grabs the last parts after propertyId

          event.view.name = pageIdentifier;
        }

        // Return true to ensure the event is sent to Datadog
        return true;
      },
    });

    // Global error handler for unhandled errors
    window.addEventListener('error', (event) => {
      if (typeof datadogRum.addError === 'function') {
        datadogRum.addError(event.error || new Error(event.message), {
          source: 'javascript',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        });
      }
    });

    // Global error handler for unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      if (typeof datadogRum.addError === 'function') {
        const reason = event && 'reason' in event ? event.reason : undefined;
        const isError = reason instanceof Error;

        let message;
        if (typeof reason === 'string') {
          message = reason;
        } else if (isError) {
          message = reason.message;
        } else if (reason && typeof reason.message === 'string') {
          message = reason.message;
        } else {
          message = String(reason);
        }

        const errorObj = isError ? reason : new Error(message);

        datadogRum.addError(errorObj, {
          source: 'promise',
          reason: message,
        });
      }
    });
  } catch (error) {
    console.error('❌ Error initializing Datadog RUM:', error);
  }
};

/**
 * Set User metadata for Datadog
 * @param user
 * @returns {void}
 */
export const setUserMetadata = ({ sanitizedUser }) => {
  if (!sanitizedUser || typeof datadogRum.setUser !== 'function') return;

  let name;
  let email;

  if (sanitizedUser.customer) {
    name = `${sanitizedUser.customer.firstName} ${sanitizedUser.customer.lastName}`;
    email = sanitizedUser.customer.emailAddress || sanitizedUser.emailAddress;
  } else if (sanitizedUser.prospect) {
    name = `${sanitizedUser.prospect.firstName} ${sanitizedUser.prospect.lastName}`;
    email = sanitizedUser.prospect.emailAddress || sanitizedUser.emailAddress;
  } else {
    name = null;
    email = sanitizedUser.emailAddress;
  }

  datadogRum.setUser({
    id: sanitizedUser.id,
    name,
    email,
    organizationId: sanitizedUser.organizationId,
    propertyId: sanitizedUser.propertyId,
    customerId: sanitizedUser.customerId ?? null,
    prospectId: sanitizedUser.prospectId ?? null,
    isActive: sanitizedUser.isActive,
    isClaimed: sanitizedUser.isClaimed,
    isVerified: sanitizedUser.isVerified,
    lastSystemAccess: sanitizedUser.lastSystemAccess,
    numberFailedLoginAttempts: sanitizedUser.numberFailedLoginAttempts,
  });
};

export const clearUserMetadata = () => {
  if (typeof datadogRum.clearUser === 'function') {
    datadogRum.clearUser();
  }
};

/**
 * Add a feature flag evaluation to Datadog
 * @param key
 * @param value
 */
export function addFeatureFlagEvaluation(key, value) {
  if (isDatadogRumAvailable()) {
    datadogRum.addFeatureFlagEvaluation(key, value);
  } else {
    console.warn('Datadog RUM not available for addFeatureFlagEvaluation');
  }
}

/**
 * Add a custom action to Datadog RUM
 * @param {string} name - The name of the action
 * @param {Object} attributes - Additional attributes for the action
 */
export const addAction = (name, attributes = {}) => {
  if (typeof datadogRum.addAction === 'function') {
    if (getIsDevelopment()) {
      console.log(`Adding action: ${name} with attributes:`, attributes);
    }
    datadogRum.addAction(name, attributes);
  }
};

/**
 * Add a custom view to Datadog RUM
 * @param {string} name - The name of the view
 * @param {Object} attributes - Additional attributes for the view
 */
export const addView = (name, attributes = {}) => {
  if (typeof datadogRum.addView === 'function') {
    datadogRum.addView(name, attributes);
  }
};

/**
 * Add a custom timing to Datadog RUM
 * @param {string} name - The name of the timing
 * @param {number} time - The time in milliseconds
 * @param {Object} attributes - Additional attributes for the timing
 */
export const addTiming = (name, time, attributes = {}) => {
  if (isDatadogRumAvailable()) {
    datadogRum.addTiming(name, time, attributes);
  } else {
    console.warn('Datadog RUM not available for addTiming');
  }
};

/**
 * Add a custom error to Datadog RUM
 * @param {Error|string} error - The error object or message
 * @param {Object} attributes - Additional attributes for the error
 */
export const addError = (error, attributes = {}) => {
  if (typeof datadogRum.addError === 'function') {
    datadogRum.addError(error, attributes);
  }
};

/**
 * Set global attributes for all RUM events
 * @param {Object} attributes - The attributes to set
 */
export const setGlobalAttributes = (attributes) => {
  if (isDatadogRumAvailable()) {
    datadogRum.setGlobalContext(attributes);
  } else {
    console.warn('Datadog RUM not available for setGlobalAttributes');
  }
};

/**
 * Set a single property in the global context
 * @param {string} key - The property key to set
 * @param {any} value - The value to set for the property
 */
export const setGlobalContextProperty = (key, value) => {
  if (isDatadogRumAvailable()) {
    datadogRum.setGlobalContextProperty(key, value);
  } else {
    console.warn('Datadog RUM not available for setGlobalContextProperty');
  }
};

/**
 * Get the current session ID
 * @returns {string} The current session ID
 */
export const getSessionId = () => {
  if (isDatadogRumAvailable()) {
    return datadogRum.getInternalContext().session_id;
  }
  return null;
};

/**
 * Get the current view ID
 * @returns {string} The current view ID
 */
export const getViewId = () => {
  if (isDatadogRumAvailable()) {
    return datadogRum.getInternalContext().view_id;
  }
  return null;
};

/**
 * Stop the current Datadog RUM session
 * @returns {void}
 */
export const stopSession = () => {
  if (isDatadogRumAvailable()) {
    console.log('Stopping Datadog RUM session');
    datadogRum.stopSession();
  } else {
    console.warn('Datadog RUM not available for stopSession');
  }
};
